import { useState, FormEvent } from "react";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value as string);
    });

    setStatus("sending");
    try {
      const res = await fetch("https://readdy.ai/api/form/d7a3erjll6jbq2vde0pg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contato" className="bg-white py-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left — Info */}
          <div>
            <span className="text-[#2563eb] text-sm font-semibold tracking-wider uppercase">Fale conosco</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f2044] mt-2 mb-4 leading-tight">
              Pronto para abrir sua empresa nos EUA?
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-10">
              Preencha o formulário e um especialista da ZB Advisor entrará em contato com você em até 24 horas. Atendimento 100% em português, sem robôs, sem respostas genéricas.
            </p>

            {/* Contact items */}
            <div className="space-y-5">
              {[
                {
                  icon: "ri-whatsapp-line",
                  label: "WhatsApp",
                  value: "+1 (407) 818-2478",
                  href: "https://wa.me/14078182478",
                },
                {
                  icon: "ri-mail-line",
                  label: "E-mail",
                  value: "contato@zbadvisor.com",
                  href: "mailto:contato@zbadvisor.com",
                },
                {
                  icon: "ri-time-line",
                  label: "Horário de atendimento",
                  value: "Seg–Sex, 9h às 18h (EST)",
                  href: null,
                },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#f0f4ff] rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-[#2563eb] text-lg`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-[#0f2044] hover:text-[#2563eb] transition-colors cursor-pointer"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-sm font-semibold text-[#0f2044]">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3 mt-10">
              {[
                { icon: "ri-shield-check-line", text: "Dados protegidos" },
                { icon: "ri-user-heart-line", text: "Atendimento humanizado" },
                { icon: "ri-timer-line", text: "Resposta em até 24h" },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-2 bg-[#f5f7fc] rounded-full px-4 py-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${b.icon} text-[#2563eb] text-sm`} />
                  </div>
                  <span className="text-xs font-medium text-[#0f2044]">{b.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-[#f5f7fc] rounded-2xl p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-check-line text-green-600 text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-[#0f2044]">Mensagem enviada!</h3>
                <p className="text-gray-500 text-sm max-w-xs">
                  Recebemos seu contato. Um especialista da ZB Advisor vai te responder em até 24 horas.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-2 text-sm text-[#2563eb] font-semibold hover:underline cursor-pointer"
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-[#0f2044] mb-6">Envie sua mensagem</h3>
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#0f2044] mb-1.5">Nome completo</label>
                      <input
                        type="text"
                        name="nome"
                        required
                        placeholder="Seu nome"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0f2044] placeholder-gray-400 focus:outline-none focus:border-[#2563eb] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0f2044] mb-1.5">WhatsApp</label>
                      <input
                        type="tel"
                        name="whatsapp"
                        required
                        placeholder="+55 (11) 99999-9999"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0f2044] placeholder-gray-400 focus:outline-none focus:border-[#2563eb] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#0f2044] mb-1.5">E-mail</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="seu@email.com"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0f2044] placeholder-gray-400 focus:outline-none focus:border-[#2563eb] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#0f2044] mb-1.5">O que você precisa?</label>
                    <select
                      name="servico"
                      required
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0f2044] focus:outline-none focus:border-[#2563eb] transition-colors cursor-pointer"
                    >
                      <option value="">Selecione um serviço</option>
                      <option value="Abrir LLC">Abrir LLC</option>
                      <option value="Abrir C-Corp">Abrir C-Corp</option>
                      <option value="Endereço Virtual">Endereço Virtual</option>
                      <option value="Conta Bancária nos EUA">Conta Bancária nos EUA</option>
                      <option value="Compliance e Relatórios">Compliance e Relatórios</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#0f2044] mb-1.5">Mensagem <span className="text-gray-400 font-normal">(opcional)</span></label>
                    <textarea
                      name="mensagem"
                      rows={4}
                      maxLength={500}
                      placeholder="Conte um pouco sobre o seu caso..."
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0f2044] placeholder-gray-400 focus:outline-none focus:border-[#2563eb] transition-colors resize-none"
                    />
                  </div>

                  {status === "error" && (
                    <p className="text-red-500 text-xs">Ocorreu um erro ao enviar. Tente novamente.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition-colors cursor-pointer text-sm whitespace-nowrap"
                  >
                    {status === "sending" ? "Enviando..." : "Falar com especialista"}
                  </button>

                  <p className="text-center text-xs text-gray-400">
                    Seus dados estão protegidos. Não enviamos spam.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
